import { Vector3 } from 'three';
import type {
    SketchTrimPowerExtendArcDefinition,
    SketchTrimPowerExtendPeriodicDefinition,
    SketchTrimPowerExtendResult,
} from '../../../../types/sketch/trim/SketchTrimPowerExtendTypes';

/**
 * 强力剪裁自然路径计算器。
 * 只计算拖动位移对应的端点，不负责实体写入、拾取或历史。
 */
export class SketchTrimPowerExtendGeometry {
    /** 完整圆周期。 */
    private static readonly FULL_TURN = Math.PI * 2;
    /** 开放圆弧与完整圆之间保留的角度容差。 */
    private static readonly ANGLE_EPSILON = 1e-7;
    /** 原起点。 */
    private readonly sourceStart = new Vector3();
    /** 原终点。 */
    private readonly sourceEnd = new Vector3();
    /** 抓取点，避免点击实体内部时端点突然跳到鼠标。 */
    private readonly anchor = new Vector3();
    /** 直线单位方向。 */
    private readonly direction = new Vector3();
    /** 周期曲线旋转后的长轴方向。 */
    private readonly axisX = new Vector3();
    /** 周期曲线旋转后的短轴方向。 */
    private readonly axisY = new Vector3();
    /** 投影计算缓存。 */
    private readonly relative = new Vector3();
    /** 本次拖动的原始周期曲线定义，直线为 null。 */
    private periodicCurve: SketchTrimPowerExtendPeriodicDefinition | null = null;
    /** 只有既有圆弧流程在极限处换侧；椭圆弧限制在开放参数域内。 */
    private switchSidesAtLimit = false;
    /** 上次鼠标角度，用于跨接缝解包。 */
    private lastAngle = 0;
    /** 累积自然角位移。 */
    private angleTravel = 0;
    /** 原圆弧正向扫掠角。 */
    private sourceSweep = 0;
    /** 高频计算复用结果。 */
    private readonly result: SketchTrimPowerExtendResult = {
        start: new Vector3(),
        end: new Vector3(),
        startAngle: 0,
        endAngle: 0,
    };

    /**
     * 固定直线支撑路径和抓取偏移。
     * @param start 原起点。
     * @param end 原终点。
     * @param anchor 鼠标抓取点。
     */
    public beginLine(start: Vector3, end: Vector3, anchor: Vector3): void {
        this.periodicCurve = null;
        this.sourceStart.copy(start);
        this.sourceEnd.copy(end);
        this.anchor.copy(anchor);
        this.direction.subVectors(end, start);
        if (this.direction.lengthSq() === 0) {
            throw new Error('强力延伸失败：零长度直线没有自然延伸方向。');
        }
        this.direction.normalize();
    }

    /**
     * 固定圆弧原圆，后续仅改变角度域。
     * @param definition 圆弧原始定义。
     * @param axisX 参考面 X 轴。
     * @param axisY 参考面 Y 轴。
     * @param anchor 鼠标抓取点。
     */
    public beginArc(
        definition: SketchTrimPowerExtendArcDefinition,
        axisX: Vector3,
        axisY: Vector3,
        anchor: Vector3,
    ): void {
        // 圆是长短半轴相等的周期曲线，参数投影和端点计算共用同一实现。
        this.beginEllipse(
            {
                centerWorld: definition.centerWorld,
                radiusX: definition.radius,
                radiusY: definition.radius,
                rotation: 0,
                startAngle: definition.startAngle,
                endAngle: definition.endAngle,
                clockwise: definition.clockwise,
            },
            axisX,
            axisY,
            anchor,
        );
        this.switchSidesAtLimit = true;
    }

    /**
     * 固定椭圆中心、半轴和朝向，只允许修改开放曲线的参数域。
     * @param definition 部分椭圆原始定义。
     * @param axisX 参考面 X 轴。
     * @param axisY 参考面 Y 轴。
     * @param anchor 鼠标抓取点。
     */
    public beginEllipse(
        definition: SketchTrimPowerExtendPeriodicDefinition,
        axisX: Vector3,
        axisY: Vector3,
        anchor: Vector3,
    ): void {
        this.periodicCurve = { ...definition, centerWorld: definition.centerWorld.clone() };
        this.switchSidesAtLimit = false;
        // 在开始时吸收椭圆旋转，后续高频投影只做向量点积与半轴归一化。
        const cosine = Math.cos(definition.rotation);
        const sine = Math.sin(definition.rotation);
        this.axisX.copy(axisX).multiplyScalar(cosine).addScaledVector(axisY, sine);
        this.axisY.copy(axisY).multiplyScalar(cosine).addScaledVector(axisX, -sine);
        this.relative.subVectors(anchor, definition.centerWorld);
        this.lastAngle = Math.atan2(
            this.relative.dot(this.axisY) / definition.radiusY,
            this.relative.dot(this.axisX) / definition.radiusX,
        );
        this.angleTravel = 0;
        const sweep = (definition.endAngle - definition.startAngle) * (definition.clockwise ? -1 : 1);
        this.sourceSweep =
            ((sweep % SketchTrimPowerExtendGeometry.FULL_TURN) + SketchTrimPowerExtendGeometry.FULL_TURN) %
            SketchTrimPowerExtendGeometry.FULL_TURN;
        if (
            definition.radiusX <= 0 ||
            definition.radiusY <= 0 ||
            this.sourceSweep <= SketchTrimPowerExtendGeometry.ANGLE_EPSILON
        ) {
            throw new Error('强力延伸失败：周期曲线必须具有正半轴和开放角度域。');
        }
    }

    /**
     * 根据鼠标在参考面上的位置计算端点。
     * @param pointer 当前鼠标投影。
     * @returns 复用的结果对象，调用方应立即消费。
     */
    public update(pointer: Vector3): SketchTrimPowerExtendResult {
        if (!this.periodicCurve) {
            // 位移只取自然路径分量，垂直偏离鼠标不会改变直线方向。
            const travel = this.relative.subVectors(pointer, this.anchor).dot(this.direction);
            this.result.start.copy(this.sourceStart).addScaledVector(this.direction, Math.min(0, travel));
            this.result.end.copy(this.sourceEnd).addScaledVector(this.direction, Math.max(0, travel));
            return this.result;
        }

        const curve = this.periodicCurve;
        const direction = curve.clockwise ? -1 : 1;
        this.relative.subVectors(pointer, curve.centerWorld);
        // 椭圆参数角不是圆心极角，必须分别除以半轴；否则非等半径曲线会延伸到错误位置。
        const localX = this.relative.dot(this.axisX) / curve.radiusX;
        const localY = this.relative.dot(this.axisY) / curve.radiusY;
        // 鼠标穿过圆心时没有有效极角，维持上一帧，避免 atan2(0,0) 导致跳变。
        if (localX * localX + localY * localY > Number.EPSILON ** 2) {
            const angle = Math.atan2(localY, localX);
            this.angleTravel +=
                Math.atan2(Math.sin(angle - this.lastAngle), Math.cos(angle - this.lastAngle)) * direction;
            this.lastAngle = angle;
        }
        const fullTurn = SketchTrimPowerExtendGeometry.FULL_TURN;
        const available = fullTurn - this.sourceSweep;
        let travel = this.angleTravel;
        if (this.switchSidesAtLimit) {
            // 保留圆弧既有的极限换侧行为，不把尚无官方依据的规则强加给椭圆弧。
            travel %= fullTurn;
            if (travel > available) travel -= fullTurn;
            else if (travel < -available) travel += fullTurn;
        }
        // 原曲线已接近完整周期时可延伸量为零，不能因负上界反向生成超过整圈的参数域。
        const availableTravel = Math.max(0, available - SketchTrimPowerExtendGeometry.ANGLE_EPSILON);
        travel = Math.max(-availableTravel, Math.min(availableTravel, travel));
        this.result.startAngle = curve.startAngle + direction * Math.min(0, travel);
        this.result.endAngle = curve.endAngle + direction * Math.max(0, travel);
        this.result.start
            .copy(curve.centerWorld)
            .addScaledVector(this.axisX, curve.radiusX * Math.cos(this.result.startAngle))
            .addScaledVector(this.axisY, curve.radiusY * Math.sin(this.result.startAngle));
        this.result.end
            .copy(curve.centerWorld)
            .addScaledVector(this.axisX, curve.radiusX * Math.cos(this.result.endAngle))
            .addScaledVector(this.axisY, curve.radiusY * Math.sin(this.result.endAngle));
        return this.result;
    }
}
